import "./DeviceSelection.scss";

import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { ListBox } from "@/components/Interface/FormFields/ListBox/ListBox";
import { Step } from "@/components/Onboarding/Steps/Step";
import { useGetDevices } from "@/services/api";
import type { Device } from "@/services/model";
import { DeviceName, deviceNameToValueMap } from "@/utils/deviceUtils";

/**
 * This is the first step of the onboarding.
 * It is used to check whether the customer wants to onboard a physical SIM (later called Tripple SIM)
 * or a eSIM. Both have their own specialities, which we will describe later.
 **/
export const DeviceSelection = () => {
  const { t } = useTranslation();
  const {
    setValue,
    register,
    watch,
    formState: { errors },
  } = useFormContext();
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>();

  // Watch for form data changes to sync with existing data
  const formDevice = watch("device");

  // Load device options
  const { data: devices, isFetching: isLoadingDevices } = useGetDevices({
    query: {
      cacheTime: 24 * 60 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  });

  // Sync local state with existing form data
  useEffect(() => {
    if (devices && formDevice && !selectedDevice) {
      const existingDevice = devices.find(
        (device) => device.description === formDevice,
      );
      if (existingDevice) {
        setSelectedDevice(existingDevice);
      }
    }
  }, [devices, formDevice, selectedDevice]);

  // Set "Smartphone" as default selection when devices are loaded (only if no existing data)
  useEffect(() => {
    if (devices && devices.length > 0 && !formDevice) {
      const smartphoneDevice = devices.find((device) =>
        device.description?.toLowerCase().includes("smartphone"),
      );
      if (smartphoneDevice) {
        setSelectedDevice(smartphoneDevice);
        // Update the form with the selected device ID
        setValue("device", smartphoneDevice.description, {
          shouldValidate: false,
        });
      }
    }
  }, [devices, setValue, formDevice]);

  // Update form when device selection changes
  useEffect(() => {
    if (selectedDevice) {
      setValue("device", selectedDevice.description, {
        shouldValidate: false,
      });
    }
  }, [selectedDevice, setValue]);

  return (
    <>
      <Step
        id="partner-device-selection"
        data-testid="select-sim"
        headline={t("partnerPortal:addDevice.step2.title")}
        subheadline={t("partnerPortal:addDevice.step2.description")}
      >
        <div className="flex w-full flex-col gap-6 py-10">
          <div>
            <h2 className="mb-2 text-lg font-semibold text-primary-100">
              {t("portal:layout.tiles.upsellSim.selectDevicePlaceholder")}
            </h2>
            <ListBox<Device>
              by="id"
              selected={selectedDevice}
              setSelected={setSelectedDevice}
              dataTestId="device-type"
              availableValues={devices ?? []}
              displayValue={(device) =>
                t(
                  `Common.devices.${
                    deviceNameToValueMap[device.description as DeviceName]
                  }`,
                )
              }
              placeholder={"Gerätetyp auswählen"}
              disabled={isLoadingDevices}
            />
          </div>

          <div id="device-name">
            <FormField
              id="device-name"
              label={t("portal:common.device.attributes.deviceName")}
              name="simAlias"
              errors={errors}
              register={register}
              type="text"
              placeholder={t("portal:common.device.attributes.deviceName")}
              options={{
                required: t("translation:label.validation.required"),
              }}
              showRequiredIndicator
            />
          </div>
        </div>
      </Step>
    </>
  );
};
